/**
 * Version card sub-components extracted from TemplateVersions.tsx so that
 * TemplateVersions stays under the 500-line hard cap.
 *
 * Exports: STATUS_VARIANT, TestVerdict, DiffPanel, VerdictBadge, VersionCard
 */
import { Clock, GitCompare, FlaskConical, ArrowUpCircle, RotateCcw, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Spinner } from '../ui/Spinner.js';
import { Can } from '../auth/index.js';
import type {
  AgentTemplateVersion,
  AgentTemplateVersionDiff,
  SubjectEvaluations,
} from '../../services/api/template-version.api.js';

export const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'info'> = {
  draft: 'warning',
  active: 'success',
  retired: 'neutral',
};

// Verdict computed from subject evaluation assignments after a test run.
export type TestVerdict = 'passed' | 'failed' | 'evaluating' | 'timed_out' | null;

// ---------- DiffPanel ----------

export function DiffPanel({ diff }: { diff: AgentTemplateVersionDiff }) {
  const content = diff.diff ?? diff.candidate ?? diff.baseline;
  if (!content) {
    return <p className="text-muted text-sm text-center py-8">No differences found.</p>;
  }
  return (
    <pre className="bg-surface-alt rounded-lg p-4 text-xs text-body overflow-auto max-h-[400px] whitespace-pre-wrap break-all">
      {JSON.stringify(content, null, 2)}
    </pre>
  );
}

// ---------- VerdictBadge ----------

export function VerdictBadge({ verdict, evalData }: { verdict: TestVerdict; evalData: SubjectEvaluations | null }) {
  if (verdict === 'evaluating') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Spinner label="Evaluating" /> Evaluating…
      </span>
    );
  }
  if (verdict === 'passed') {
    const summary = evalData?.assignments.map((a) => a.promotionVerdict.reason).join(', ');
    return (
      <span title={summary} className="inline-flex items-center gap-1 text-xs text-success font-medium">
        <CheckCircle2 size={12} /> Gate passed
      </span>
    );
  }
  if (verdict === 'failed') {
    const missing = evalData?.assignments.flatMap((a) => a.promotionVerdict.satisfaction?.missingKeys ?? []).join(', ');
    return (
      <span
        title={missing ? `Missing: ${missing}` : undefined}
        className="inline-flex items-center gap-1 text-xs text-danger font-medium"
      >
        <XCircle size={12} /> Gate failed{missing ? `: ${missing}` : ''}
      </span>
    );
  }
  if (verdict === 'timed_out') {
    return (
      <span
        title="The evaluation gate is taking longer than expected. It may still be running — re-run Test to check again."
        className="inline-flex items-center gap-1 text-xs text-warning font-medium"
      >
        <Clock size={12} /> Still running — re-test to check
      </span>
    );
  }
  return null;
}

// ---------- VersionCard ----------

interface VersionCardProps {
  version: AgentTemplateVersion;
  activeVersion: AgentTemplateVersion | null;
  verdict: TestVerdict;
  evalData: SubjectEvaluations | null;
  onPromote: () => void;
  onRollback: () => void;
  onDiff: () => void;
  onTest: () => void;
  isPromoting: boolean;
  isRollingBack: boolean;
  isTesting: boolean;
}

export function VersionCard({
  version,
  activeVersion,
  verdict,
  evalData,
  onPromote,
  onRollback,
  onDiff,
  onTest,
  isPromoting,
  isRollingBack,
  isTesting,
}: VersionCardProps) {
  const variant = STATUS_VARIANT[version.status] ?? 'neutral';
  const isActive = version.status === 'active';
  const isDraft = version.status === 'draft';
  const hash = version.systemPromptHash ? version.systemPromptHash.slice(0, 8) : null;
  const canDiff = !isActive && !!activeVersion;

  return (
    <div className={`bg-surface border rounded-lg p-4 transition-colors ${isActive ? 'border-success/30' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-heading">v{version.version}</span>
            <Badge variant={variant} size="md">
              {version.status}
            </Badge>
            {hash && (
              <span className="text-xs font-mono text-faint bg-surface-alt px-1.5 py-0.5 rounded-sm">{hash}</span>
            )}
          </div>
          <div className="text-sm">
            {version.changeSummary && <p className="text-body">{version.changeSummary}</p>}
            <div className="flex items-center gap-3 text-xs text-muted mt-1">
              <span className="inline-flex items-center gap-1">
                <Clock size={11} />
                Created {new Date(version.createdAt).toLocaleDateString()}
              </span>
              {/* promotedAt/retiredAt come back as `unknown` from codegen (nullable date-or-string
                  union collapses); they are ISO strings at runtime whenever truthy. */}
              {version.promotedAt != null && (
                <span>Promoted {new Date(version.promotedAt as string).toLocaleDateString()}</span>
              )}
              {version.retiredAt != null && (
                <span>Retired {new Date(version.retiredAt as string).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Diff button — any non-active version when an active version exists */}
          {canDiff && (
            <Button size="sm" variant="ghost" onClick={onDiff} title="Diff against active version">
              <GitCompare size={14} />
            </Button>
          )}

          {/* Test button — draft only, gated */}
          {isDraft && (
            <Can policy="CanManageAgents">
              <Button
                size="sm"
                variant="secondary"
                onClick={onTest}
                loading={isTesting}
                disabled={isTesting || verdict === 'evaluating'}
                title="Run evaluation gate"
              >
                <FlaskConical size={14} className="mr-1" />
                Test
              </Button>
            </Can>
          )}

          {/* Promote — draft only, gated */}
          {isDraft && (
            <Can policy="CanManageAgents">
              <Button size="sm" variant="success" onClick={onPromote} loading={isPromoting} disabled={isPromoting}>
                <ArrowUpCircle size={14} className="mr-1" />
                Promote
              </Button>
            </Can>
          )}

          {/* Rollback — retired only */}
          {version.status === 'retired' && (
            <Button size="sm" variant="secondary" onClick={onRollback} loading={isRollingBack} disabled={isRollingBack}>
              <RotateCcw size={14} className="mr-1" />
              Rollback
            </Button>
          )}
        </div>
      </div>

      {/* Verdict row below card body */}
      {verdict !== null && (
        <div className="mt-2 pt-2 border-t">
          <VerdictBadge verdict={verdict} evalData={evalData} />
        </div>
      )}
    </div>
  );
}
